import type { LitNodeClientNodeJs } from "@lit-protocol/lit-node-client"
import type { SessionSigsMap } from "@lit-protocol/types"
import { MEMOS, NAMADA_KEY_TYPE } from "../constants"
import { ExtendedStoredKeyMetadata } from "./types"
import { listEncryptedKeyMetadata } from "@lit-protocol/wrapped-keys/src/lib/api/list-encrypted-key-metadata"

const NO_KEYS_ERROR_MESSAGE = 'no keys exist for pkpaddress'

/**
 * Lists ALL wrapped keys for a given PKP.
 * If no keys exist for the given PKP, returns an empty array instead of an error.
 *
 * @param litNodeClient 
 * @param pkpSessionSigs 
 * @returns 
 */
export async function listEncryptedKeyMetadataSafe(litNodeClient: LitNodeClientNodeJs, pkpSessionSigs: SessionSigsMap): Promise<ExtendedStoredKeyMetadata[]> {
    try {
      const keys = await listEncryptedKeyMetadata({
        pkpSessionSigs,
        litNodeClient,
      })
      return keys.map(key => ({
        ...key,
        keyType: key.memo.includes('namada') ? NAMADA_KEY_TYPE : key.keyType,
      }))
    } catch (error) {
        if (error instanceof Error && error.message.toLowerCase().includes(NO_KEYS_ERROR_MESSAGE)) {
            return []
        }
  
        throw error
    }
  }

/**
 * This function simply re-exports the listEncryptedKeyMetadata function from the wrapped-keys library.
 * It is used to list all the encrypted keys for a given PKP.
 *
 * Returned wrapped keys are sorted by id
 *
 * Doing this just to keep the handlers convention.
 */
export async function listWrappedKeys(
    litNodeClient: LitNodeClientNodeJs,
    pkpSessionSigs: SessionSigsMap,
  ): Promise<ExtendedStoredKeyMetadata[]> {
      const encryptedKeyMetadata = await listEncryptedKeyMetadataSafe(litNodeClient, pkpSessionSigs)

  
      // Sort first, then deduplicate by keyType to keep the lowest id for each keyType
      // Filter for Polaris keys (wallet keys and runner secrets)
      const sortedKeys = encryptedKeyMetadata
        .filter(
          (key) =>
            key?.memo?.includes('polaris-wallet') ||
            key?.memo?.includes('polaris-runner') ||
            key?.memo?.includes(MEMOS.rootKeyPrefix) ||
            key?.memo?.includes(MEMOS.namadaWrappedKeyPrefix) ||
            key?.memo?.includes(MEMOS.derivedRunnerSecretPrefix),
        )
        .sort((a, b) => {
          return a.id > b.id ? 1 : -1
        })
  
      return sortedKeys
  }